import { removeCalendarEventsFromEventsCacheMatchingFilter } from '../actions/eventsCacheActions';
import { CalendarEventOperationResult, instantCalendarEventDelete } from '../actions/publicActions';
import { getCalendarEventWithId } from '../selectors/calendarFolderEventsSelectors';
import { isEventsCacheInitialized } from '../selectors/eventsCacheSelectors';
import getItemIdToUpdate from '../utils/getItemIdToUpdate';
import { reloadItemsCache } from '../utils/SharedCacheFunctions';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { isBefore } from 'owa-datetime';
import EventScope from 'owa-service/lib/contract/EventScope';
import { orchestrator } from 'satcheljs';

orchestrator(instantCalendarEventDelete, async actionMessage => {
    const {
        eventId,
        eventScope,
        dataPoint,
        actionToExecute,
        onEventDeleteSuccess,
        onEventDeleteFailed,
        responseSent,
        actionSource,
        errorMessage,
        asyncUndoAction,
    } = actionMessage;

    const eventToDelete = getCalendarEventWithId(eventId.Id);
    let allowLocalDelete = isInstantDeleteSupported(eventToDelete);
    onDeleteItemStart(eventToDelete, eventScope, allowLocalDelete);

    if (dataPoint) {
        dataPoint.markUserPerceivedTime(); // mark user perceived time as soon as local lie is done
    }

    // Execute the operation
    let idToDelete = getItemIdToUpdate(eventId.Id, eventScope);
    let actionResult: CalendarEventOperationResult = null;

    try {
        // this notification gives the user a chance to cancel their action before actually sending to the server
        // if shouldUndo returns true, we return back without calling actionToExecute
        let shouldUndo = await asyncUndoAction(eventToDelete, responseSent, actionSource);

        if (shouldUndo) {
            undoDelete(eventToDelete, allowLocalDelete);
            return;
        }
        actionResult = await actionToExecute(idToDelete);

        if (!actionResult.isSuccessful) {
            throw errorMessage;
        }
    } catch (error) {
        undoDelete(eventToDelete, allowLocalDelete);
        let retryFunction = () =>
            instantCalendarEventDelete(
                eventId,
                eventScope,
                null,
                actionToExecute,
                onEventDeleteSuccess,
                () => onEventDeleteFailed(error, null, actionSource),
                actionSource,
                errorMessage,
                responseSent
            );
        onEventDeleteFailed(error, retryFunction, actionSource);
        throw error;
    }

    onEventDeleteSuccess();
});

function isInstantDeleteSupported(item: CalendarEvent): boolean {
    // We do not support instant delete for unselected calendars that have no cache.
    return item && isEventsCacheInitialized(item.ParentFolderId.Id);
}

function onDeleteItemStart(
    itemToDelete: CalendarEvent,
    eventScope: EventScope,
    allowLocalDelete: boolean
) {
    if (allowLocalDelete) {
        // Remove item/series from cache
        const folderId = itemToDelete.ParentFolderId.Id;
        if (
            eventScope == EventScope.AllInstancesInSeries ||
            eventScope == EventScope.ThisAndFollowingInstances
        ) {
            let start = itemToDelete.Start;
            removeCalendarEventsFromEventsCacheMatchingFilter(
                folderId,
                item =>
                    item.InstanceKey == itemToDelete.InstanceKey &&
                    (eventScope == EventScope.AllInstancesInSeries ||
                        (eventScope == EventScope.ThisAndFollowingInstances &&
                            !isBefore(item.Start, start)))
            );
        } else {
            removeCalendarEventsFromEventsCacheMatchingFilter(
                folderId,
                event => event.ItemId.Id === itemToDelete.ItemId.Id
            );
        }
    }
}

function undoDelete(itemToDelete: CalendarEvent, allowLocalDelete: boolean) {
    if (allowLocalDelete) {
        reloadItemsCache(itemToDelete.ParentFolderId.Id);
    }
}
